import type { MechanicDefinition } from "./types";

export const messageEditing: MechanicDefinition = {
  id: "message_editing",
  name: "Retry & Edit",
  description: "Controls whether players can retry, edit, or regenerate messages during play",
  icon: "RotateCcw",
  category: "core",
  aiHints: {
    purpose: "Controls whether players can retry, edit, or regenerate their messages and AI responses.",
    whenToUse: "Consider for all games. Disable retries for hardcore/permadeath experiences.",
    synergies: [],
    conflicts: [],
    prerequisites: [],
    tags: ["retry", "edit", "undo", "regenerate", "core"],
    complexity: 1,
    exampleConfig: { allow_retry: true, allow_edit: true },
    stateVariables: [],
  },
  configSchema: [
    {
      key: "retry_level",
      label: "Editing Level",
      type: "select",
      options: [
        {
          value: "none",
          label: "No Retry",
          description: "Players cannot retry or edit any messages. Every action is final — consequences are permanent.",
        },
        {
          value: "retry_last",
          label: "Retry Last AI Message",
          description: "Players can regenerate the last AI response only. Their own message stays the same but the narrator re-rolls the outcome.",
        },
        {
          value: "edit_own",
          label: "Edit My Last Message",
          description: "Players can change their last action and regenerate the AI response. The AI's previous reply is removed and a fresh response is generated for the edited input.",
        },
        {
          value: "edit_both",
          label: "Edit Last Messages",
          description: "Players can edit both their last message and the AI's response, then trigger a full regeneration. Useful for correcting misunderstandings.",
        },
        {
          value: "edit_all",
          label: "Edit All",
          description: "Full conversation editing — delete, edit, or regenerate any message in the history. Does NOT regenerate a tree of subsequent actions; only the targeted message is affected.",
        },
      ],
      default: "retry_last",
      description: "How much control the player has over message history",
    },
    {
      key: "max_retries_per_turn",
      label: "Max Retries Per Turn",
      type: "number",
      default: 0,
      description: "Maximum number of retries allowed per turn. 0 = unlimited.",
      condition: { key: "retry_level", value: "retry_last" },
    },
    {
      key: "cooldown_warning",
      label: "Show Undo Warning",
      type: "boolean",
      default: true,
      description: "Show a confirmation dialog before editing or deleting messages to prevent accidents",
    },
  ],
  systemPromptSection: (config) => {
    const level = config.retry_level || "retry_last";

    if (level === "none") {
      return `## Retry & Edit Rules
The player has NO retry or edit capability. Every action is FINAL. Narrate with full commitment — there are no take-backs. If the player asks to undo or retry, remind them (in-character) that actions have consequences.`;
    }

    const descriptions: Record<string, string> = {
      retry_last: "The player can ask the narrator to RE-TELL the last response differently. The player's action stays the same but the outcome may change. When a retry is triggered, you should vary your response meaningfully — don't just rephrase the same outcome.",
      edit_own: "The player can CHANGE their last action and get a fresh AI response. When this happens, treat the new action as if it was the original — forget the previous version entirely.",
      edit_both: "The player can edit both their last message AND your response, then trigger regeneration. Treat edited content as canonical — the previous versions never happened.",
      edit_all: "The player has FULL editing capability over the conversation history. Any message can be edited or deleted. Treat the current conversation state as the only truth — deleted messages never happened, edited messages always said what they say now.",
    };

    return `## Retry & Edit Rules
${descriptions[level]}
${config.max_retries_per_turn && level === "retry_last" ? `Maximum ${config.max_retries_per_turn} retries per turn.` : ""}
When regenerating, vary your response meaningfully — different dice interpretations, different NPC reactions, different environmental details. Don't just rephrase the same outcome.`;
  },
  tools: [],
  initializeState: (config) => ({
    _retry_level: config.retry_level || "retry_last",
    _retries_this_turn: 0,
  }),
};
